import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import Avatar from '../components/Avatar';
import { getImageUrl } from '../utils/imageUrl';
import { prepareImageForUpload } from '../utils/imageUpload';
import {
  uploadPlanterHeadshot,
  uploadPlanterFamilyPhoto,
  deletePlanterHeadshot,
  deletePlanterFamilyPhoto,
  updatePlanter,
  deletePlanter,
  promotePlanterFromGrowth,
  completePlanterTraining,
  setPlanterFundingEnd,
  advancePlanterToPostFunding,
  revertPlanterToFunding,
  revertPlanterToTraining,
  endPlanterJourney,
  formatJourneyEndReasonLabel,
  JourneyEndReason,
} from '../api/plantersService';
import { 
  getPlanter, 
  getPlanterContacts, 
  getPlanterMbcbReports,
  getMbcbReportDetail,
  createMbcbReport,
  sendMbcbReportReminder,
  createContact,
  updateContact,
  deleteContact,
  sendPlanterAppInvite,
  PlanterContact,
  Planter,
  PlanterContactType,
  MbcbReportDetail,
  GivingBreakdown,
  formatPlanterLocation,
} from '../api/plantersService';
import { usersService, HubUser } from '../api/usersService';
import { useAuth } from '../contexts/AuthContext';
import {
  canEditMbcbReport,
  formatMbcbReportPeriod,
  mbcbReportActionLabel,
  mbcbReportStatusLabel,
} from '../utils/mbcbReports';

const axiosErrorMessage = (err: unknown): string => {
  const ax = err as { response?: { data?: { message?: string }; status?: number }; message?: string };
  const msg = ax?.response?.data?.message;
  if (typeof msg === 'string' && msg.length > 0) return msg;
  const status = ax?.response?.status;
  if (status === 404) return 'Church planter not found.';
  if (status) return `Request failed (${status}).`;
  if (err instanceof Error && err.message) return err.message;
  return 'Could not load planter.';
};

const existingReportIdFromConflict = (err: unknown): number | null => {
  const ax = err as {
    response?: {
      status?: number;
      data?: { code?: string; data?: { existingReportId?: number } };
    };
  };
  if (ax?.response?.status !== 409 || ax.response.data?.code !== 'REPORT_PERIOD_EXISTS') return null;
  return ax.response.data.data?.existingReportId ?? null;
};

const SUNDAY_KEYS = ['s1', 's2', 's3', 's4', 's5'] as const;
const moneyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const percentFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 1,
});
const incomeSourceLabels: Array<{ key: keyof GivingBreakdown['income']; label: string }> = [
  { key: 'congregationalOffering', label: 'Congregational offering' },
  { key: 'sponsoringChurches', label: 'Sponsoring churches' },
  { key: 'associations', label: 'Associations' },
  { key: 'mbcb', label: 'MBCB support' },
  { key: 'otherSupport', label: 'Other support' },
];
const sourceChartColors = ['#2563eb', '#0f766e', '#f59e0b', '#7c3aed', '#ef4444'];
const contactTypeOptions: Array<{ value: PlanterContactType; label: string }> = [
  { value: 'phone', label: 'Phone Call' },
  { value: 'text', label: 'Text Message' },
  { value: 'email', label: 'Email' },
  { value: 'in-person', label: 'In-Person' },
  { value: 'other', label: 'Other' },
];
const isPlanterContactType = (value: string): value is PlanterContactType =>
  contactTypeOptions.some((option) => option.value === value);

const END_JOURNEY_OPTIONS: { reason: JourneyEndReason; label: string }[] = [
  { reason: 'quit', label: 'Quit' },
  { reason: 'fired', label: 'Fired' },
  { reason: 'retired', label: 'Retired' },
  { reason: 'moved_on', label: 'Moved on' },
  { reason: 'other', label: 'Other' },
];

const formatMonthYear = (month?: number | null, year?: number | null) => {
  return formatMbcbReportPeriod(month, year);
};

const formatMoney = (value?: number | null) => {
  if (value == null || Number.isNaN(value)) return '—';
  return moneyFormatter.format(value);
};

const formatPercent = (value?: number | null) => {
  if (value == null || Number.isNaN(value)) return '—';
  return `${percentFormatter.format(value)}%`;
};

const toFiniteNumber = (value: unknown) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const getSundaysInMonth = (month: number, year: number) => {
  let sundays = 0;
  const lastDay = new Date(year, month, 0).getDate();
  for (let day = 1; day <= lastDay; day += 1) {
    if (new Date(year, month - 1, day).getDay() === 0) sundays += 1;
  }
  return Math.max(sundays, 1);
};

const getAttendanceAverage = (report: MbcbReportDetail) => {
  const worshipAttendance = report.weeklyMetrics?.worshipAttendance;
  if (worshipAttendance) {
    const values = SUNDAY_KEYS
      .map((key) => worshipAttendance[key])
      .filter((value): value is number => typeof value === 'number' && !Number.isNaN(value));
    const nonZeroValues = values.filter((value) => value > 0);
    if (nonZeroValues.length > 0) {
      return nonZeroValues.reduce((sum, value) => sum + value, 0) / nonZeroValues.length;
    }
  }
  const sundayAttendance = toFiniteNumber(report.sundayAttendance);
  if (sundayAttendance > 0) {
    return sundayAttendance / getSundaysInMonth(report.reportMonth, report.reportYear);
  }
  return 0;
};

const getIncomeTotal = (report: MbcbReportDetail) => {
  const nestedTotal = toFiniteNumber(report.givingBreakdown?.income?.total);
  if (nestedTotal > 0) return nestedTotal;

  const monthlyIncome = toFiniteNumber(report.monthlyIncome);
  if (monthlyIncome > 0) return monthlyIncome;

  const fallbackSum = incomeSourceLabels.reduce(
    (sum, source) => sum + toFiniteNumber(report.givingBreakdown?.income?.[source.key]),
    0,
  );
  return fallbackSum;
};

const getCpGiving = (report: MbcbReportDetail) =>
  toFiniteNumber(report.givingBreakdown?.missionGiving?.cooperativeProgram);

const plantingStepLabel = (step?: string | null) => {
  if (!step) return '—';
  const labels: Record<string, string> = {
    training: 'Training',
    funding: 'Funding',
    post_funding: 'Post-funding',
  };
  return labels[step] || step;
};

const statusClassName = (status?: string | null) => {
  switch (status) {
    case 'approved':
      return 'bg-green-100 text-green-800';
    case 'submitted':
      return 'bg-blue-100 text-blue-800';
    case 'needs_revision':
    case 'returned':
      return 'bg-amber-100 text-amber-800';
    case 'draft':
      return 'bg-gray-100 text-gray-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const AttendanceTrendChart: React.FC<{ points: Array<{ label: string; value: number }> }> = ({ points }) => {
  if (!points.length) {
    return <div className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-sm text-gray-500">No attendance trend data yet.</div>;
  }

  const width = 420;
  const height = 150;
  const padding = 18;
  const maxValue = Math.max(...points.map((point) => point.value), 1);
  const minValue = Math.min(...points.map((point) => point.value), 0);
  const range = Math.max(maxValue - minValue, 1);
  const step = points.length > 1 ? (width - padding * 2) / (points.length - 1) : 0;

  const coordinates = points.map((point, index) => {
    const x = padding + (step * index);
    const y = height - padding - (((point.value - minValue) / range) * (height - padding * 2));
    return { x, y, ...point };
  });

  const polylinePoints = coordinates.map((point) => `${point.x},${point.y}`).join(' ');

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <svg viewBox={`0 0 ${width} ${height}`} className="h-40 w-full">
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#d1d5db" strokeWidth="1" />
        <polyline fill="none" stroke="#2563eb" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" points={polylinePoints} />
        {coordinates.map((point) => (
          <g key={point.label}>
            <circle cx={point.x} cy={point.y} r="4.5" fill="#2563eb" />
          </g>
        ))}
      </svg>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-500 sm:grid-cols-4">
        {points.map((point) => (
          <div key={point.label} className="rounded-md bg-white px-2 py-1.5">
            <div className="font-medium text-gray-700">{point.label}</div>
            <div>{Math.round(point.value)} avg</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const GivingBreakdownPieChart: React.FC<{
  slices: Array<{ label: string; value: number }>;
  total: number;
}> = ({ slices, total }) => {
  const chartSlices = slices.filter((slice) => slice.value > 0);

  if (!chartSlices.length || total <= 0) {
    return <div className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-sm text-gray-500">No giving data yet.</div>;
  }

  const radius = 72;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex flex-col items-center gap-5 lg:flex-row lg:items-start">
        <div className="relative flex-shrink-0">
          <svg viewBox="0 0 180 180" className="h-48 w-48 -rotate-90">
            <circle cx="90" cy="90" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="24" />
            {chartSlices.map((slice, index) => {
              const dash = (slice.value / total) * circumference;
              const segment = (
                <circle
                  key={slice.label}
                  cx="90"
                  cy="90"
                  r={radius}
                  fill="none"
                  stroke={sourceChartColors[index % sourceChartColors.length]}
                  strokeWidth="24"
                  strokeDasharray={`${dash} ${circumference - dash}`}
                  strokeDashoffset={-offset}
                  strokeLinecap="butt"
                />
              );
              offset += dash;
              return segment;
            })}
          </svg>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Total received</div>
            <div className="mt-1 text-xl font-bold text-gray-900">{formatMoney(total)}</div>
          </div>
        </div>

        <div className="w-full space-y-3">
          {slices.map((slice, index) => {
            const share = total > 0 ? (slice.value / total) * 100 : 0;
            return (
              <div key={slice.label} className="flex items-start justify-between gap-3 rounded-md bg-gray-50 px-3 py-2">
                <div className="flex items-start gap-2">
                  <span
                    className="mt-1 h-3 w-3 rounded-full"
                    style={{ backgroundColor: sourceChartColors[index % sourceChartColors.length] }}
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-800">{slice.label}</div>
                    <div className="text-xs text-gray-500">{formatPercent(share)} of total received</div>
                  </div>
                </div>
                <div className="text-sm font-semibold text-gray-900">{formatMoney(slice.value)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const useIsLg = () => {
  const [isLg, setIsLg] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setIsLg(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isLg;
};

const PlanterDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const planterId = Number.parseInt(id ?? '', 10);
  const planterIdValid = Number.isFinite(planterId) && planterId > 0;

  const hasRole = (roleName: string) => {
    return !!user?.roles?.some((r: any) => (typeof r === 'string' ? r : r.name) === roleName);
  };
  const canEdit = hasRole('admin') || hasRole('staff') || hasRole('hub_leader');
  const canDelete = hasRole('admin');
  const isStaffish = hasRole('admin') || hasRole('staff') || hasRole('mbcb');

  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState<PlanterContact | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddReportForm, setShowAddReportForm] = useState(false);
  const [addReportMessage, setAddReportMessage] = useState<string | null>(null);
  const [reportReminderMessage, setReportReminderMessage] = useState<string | null>(null);
  const [photoUploadError, setPhotoUploadError] = useState<{ headshot: string | null; family: string | null }>({
    headshot: null,
    family: null,
  });
  const [photoUploadLoading, setPhotoUploadLoading] = useState<{ headshot: boolean; family: boolean }>({
    headshot: false,
    family: false,
  });
  
  const [editForm, setEditForm] = useState({
    name: '',
    spouseName: '',
    email: '',
    phone: '',
    secondaryPhone: '',
    city: '',
    state: '',
    churchName: '',
    mailingAddressStreet: '',
    mailingAddressStreet2: '',
    mailingAddressCity: '',
    mailingAddressState: '',
    mailingAddressZip: '',
    mailingAddressCountry: '',
    assignedCoachId: '' as string,
  });
  
  const [contactForm, setContactForm] = useState({
    contactDate: new Date().toISOString().split('T')[0],
    contactType: 'phone' as PlanterContactType,
    notes: '',
    followUpDate: '',
  });
  const [fundingEndInput, setFundingEndInput] = useState('');
  const [pipelineBootstrapForm, setPipelineBootstrapForm] = useState({
    pathKind: '' as '' | 'growth' | 'planting',
    plantingStep: 'training' as 'training' | 'funding' | 'post_funding',
    fundingEndDate: '',
  });
  const [pipelineMessage, setPipelineMessage] = useState<string | null>(null);
  const endJourneyMenuRef = useRef<HTMLDivElement>(null);
  const [endJourneyMenuOpen, setEndJourneyMenuOpen] = useState(false);
  const [showEndJourneyOtherModal, setShowEndJourneyOtherModal] = useState(false);
  const [endJourneyOtherNotes, setEndJourneyOtherNotes] = useState('');

  const [newReportForm, setNewReportForm] = useState({
    reportMonth: new Date().getMonth() + 1,
    reportYear: new Date().getFullYear(),
  });

  const isLg = useIsLg();

  // Fetch planter data
  const {
    data: planter,
    isLoading: planterLoading,
    isError: planterQueryError,
    error: planterFetchError,
  } = useQuery({
    queryKey: ['planter', planterId],
    queryFn: () => getPlanter(planterId),
    enabled: planterIdValid,
  });

  useEffect(() => {
    if (planter?.fundingEndDate) {
      setFundingEndInput(String(planter.fundingEndDate).slice(0, 10));
    } else {
      setFundingEndInput('');
    }
  }, [planter?.fundingEndDate]);

  useEffect(() => {
    if (!endJourneyMenuOpen) return;
    const handle = (e: MouseEvent) => {
      if (endJourneyMenuRef.current && !endJourneyMenuRef.current.contains(e.target as Node)) {
        setEndJourneyMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [endJourneyMenuOpen]);

  // Fetch contacts
  const { data: contacts, isLoading: contactsLoading } = useQuery({
    queryKey: ['planterContacts', planterId],
    queryFn: () => getPlanterContacts(planterId),
    enabled: planterIdValid,
  });

  const { data: mbcbReports, isLoading: mbcbLoading } = useQuery({
    queryKey: ['planterMbcbReports', planterId],
    queryFn: () => getPlanterMbcbReports(planterId),
    enabled: planterIdValid,
  });
  const sortedMbcbReports = useMemo(
    () =>
      (mbcbReports || [])
        .slice()
        .sort((a, b) => (b.reportYear - a.reportYear) || (b.reportMonth - a.reportMonth)),
    [mbcbReports],
  );
  const { data: mbcbReportDetails, isLoading: mbcbReportDetailsLoading } = useQuery({
    queryKey: ['planterMbcbReportDetails', planterId, sortedMbcbReports.map((report) => report.id)],
    queryFn: () => Promise.all(sortedMbcbReports.map((report) => getMbcbReportDetail(report.id))),
    enabled: planterIdValid && sortedMbcbReports.length > 0,
  });
  const detailedReports = useMemo(
    () =>
      (mbcbReportDetails || [])
        .slice()
        .sort((a, b) => (b.reportYear - a.reportYear) || (b.reportMonth - a.reportMonth)),
    [mbcbReportDetails],
  );
  const reportInsights = useMemo(() => {
    if (!detailedReports.length) {
      return null;
    }

    const attendanceSeries = detailedReports
      .slice()
      .reverse()
      .map((report) => ({
        label: formatMonthYear(report.reportMonth, report.reportYear),
        value: getAttendanceAverage(report),
      }));

    const attendanceAverage = attendanceSeries.reduce((sum, point) => sum + point.value, 0) / attendanceSeries.length;
    const totalReceived = detailedReports.reduce((sum, report) => sum + getIncomeTotal(report), 0);
    const cpGiving = detailedReports.reduce((sum, report) => sum + getCpGiving(report), 0);
    const sourceBreakdown = incomeSourceLabels.map((source) => ({
      ...source,
      value: detailedReports.reduce((sum, report) => sum + toFiniteNumber(report.givingBreakdown?.income?.[source.key]), 0),
    }));

    return {
      latestReport: detailedReports[0],
      attendanceAverage,
      attendanceSeries,
      totalReceived,
      cpGiving,
      cpPercentageOfReceived: totalReceived > 0 ? (cpGiving / totalReceived) * 100 : null,
      sourceBreakdown,
      reportCount: detailedReports.length,
    };
  }, [detailedReports]);

  // Fetch coaches (only when edit modal is open)
  const { data: coachesData } = useQuery({
    queryKey: ['coaches'],
    queryFn: () => usersService.getUsersByRole('coach'),
    enabled: showEditModal,
  });
  const coaches: HubUser[] = coachesData?.data || [];

  // Mutations
  const createContactMutation = useMutation({
    mutationFn: (data: typeof contactForm) => createContact(planterId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planterContacts', planterId] });
      queryClient.invalidateQueries({ queryKey: ['planters'] });
      setShowContactForm(false);
      resetContactForm();
    },
  });

  const updateContactMutation = useMutation({
    mutationFn: ({ contactId, data }: { contactId: number; data: Partial<PlanterContact> }) => 
      updateContact(planterId, contactId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planterContacts', planterId] });
      setEditingContact(null);
      resetContactForm();
    },
  });

  const deleteContactMutation = useMutation({
    mutationFn: (contactId: number) => deleteContact(planterId, contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planterContacts', planterId] });
    },
  });

  const updatePlanterMutation = useMutation({
    mutationFn: (data: Partial<Planter>) => updatePlanter(planterId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planter', planterId] });
      queryClient.invalidateQueries({ queryKey: ['planters'] });
      setShowEditModal(false);
    },
  });

  const bootstrapPipelineMutation = useMutation({
    mutationFn: (data: Partial<Planter>) => updatePlanter(planterId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planter', planterId] });
      queryClient.invalidateQueries({ queryKey: ['planters'] });
      setPipelineMessage(null);
    },
    onError: (e) => setPipelineMessage(axiosErrorMessage(e)),
  });

  const deletePlanterMutation = useMutation({
    mutationFn: () => deletePlanter(planterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planters'] });
      navigate('/planters');
    },
  });

  const inviteAppMutation = useMutation({
    mutationFn: () => sendPlanterAppInvite(planterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planter', planterId] });
    },
  });

  const invalidatePlanterQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['planter', planterId] });
    queryClient.invalidateQueries({ queryKey: ['planters'] });
  };

  const promoteFromGrowthMutation = useMutation({
    mutationFn: () => promotePlanterFromGrowth(planterId),
    onSuccess: () => {
      invalidatePlanterQueries();
      setPipelineMessage(null);
    },
    onError: (e) => setPipelineMessage(axiosErrorMessage(e)),
  });

  const completeTrainingMutation = useMutation({
    mutationFn: () => completePlanterTraining(planterId),
    onSuccess: () => {
      invalidatePlanterQueries();
      setPipelineMessage(null);
    },
    onError: (e) => setPipelineMessage(axiosErrorMessage(e)),
  });

  const setFundingEndMutation = useMutation({
    mutationFn: () => setPlanterFundingEnd(planterId, { fundingEndDate: fundingEndInput }),
    onSuccess: () => {
      invalidatePlanterQueries();
      setPipelineMessage(null);
    },
    onError: (e) => setPipelineMessage(axiosErrorMessage(e)),
  });

  const advancePostFundingMutation = useMutation({
    mutationFn: () => advancePlanterToPostFunding(planterId),
    onSuccess: () => {
      invalidatePlanterQueries();
      setPipelineMessage(null);
    },
    onError: (e) => setPipelineMessage(axiosErrorMessage(e)),
  });

  const revertToFundingMutation = useMutation({
    mutationFn: () => revertPlanterToFunding(planterId),
    onSuccess: () => {
      invalidatePlanterQueries();
      setPipelineMessage(null);
    },
    onError: (e) => setPipelineMessage(axiosErrorMessage(e)),
  });

  const revertToTrainingMutation = useMutation({
    mutationFn: () => revertPlanterToTraining(planterId),
    onSuccess: () => {
      invalidatePlanterQueries();
      setPipelineMessage(null);
    },
    onError: (e) => setPipelineMessage(axiosErrorMessage(e)),
  });

  const endJourneyMutation = useMutation({
    mutationFn: (payload: { reason: JourneyEndReason; notes?: string }) =>
      endPlanterJourney(planterId, payload),
    onSuccess: () => {
      invalidatePlanterQueries();
      setPipelineMessage(null);
      setShowEndJourneyOtherModal(false);
      setEndJourneyOtherNotes('');
    },
    onError: (e) => setPipelineMessage(axiosErrorMessage(e)),
  });

  const createReportMutation = useMutation({
    mutationFn: () =>
      createMbcbReport({
        planterId,
        reportMonth: newReportForm.reportMonth,
        reportYear: newReportForm.reportYear,
      }),
    onSuccess: (report) => {
      queryClient.invalidateQueries({ queryKey: ['planterMbcbReports', planterId] });
      setAddReportMessage(null);
      setShowAddReportForm(false);
      navigate(`/planters/${planterId}/reports/${report.id}/edit`);
    },
    onError: (error) => {
      const existingReportId = existingReportIdFromConflict(error);
      if (existingReportId != null) {
        setAddReportMessage('A report already exists for that month. Opening the existing report.');
        setShowAddReportForm(false);
        navigate(`/planters/${planterId}/reports/${existingReportId}/edit`);
        return;
      }
      setAddReportMessage(axiosErrorMessage(error));
    },
  });

  const sendReminderMutation = useMutation({
    mutationFn: (reportId: number) => sendMbcbReportReminder(reportId),
    onSuccess: (data) => {
      const pushCount = data.push.successfulSends;
      const deliverySummary = data.emailSent
        ? `Email sent and push queued for ${pushCount} device${pushCount === 1 ? '' : 's'}.`
        : `Push queued for ${pushCount} device${pushCount === 1 ? '' : 's'}.`;
      setReportReminderMessage(deliverySummary);
    },
    onError: (error) => {
      setReportReminderMessage(axiosErrorMessage(error));
    },
  });

  const resetContactForm = () => {
    setContactForm({
      contactDate: new Date().toISOString().split('T')[0],
      contactType: 'phone',
      notes: '',
      followUpDate: '',
    });
  };

  const handleSubmitContact = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingContact) {
      updateContactMutation.mutate({ 
        contactId: editingContact.id, 
        data: contactForm 
      });
    } else {
      createContactMutation.mutate(contactForm);
    }
  };

  const handleEditContact = (contact: PlanterContact) => {
    setEditingContact(contact);
    setContactForm({
      contactDate: contact.contactDate.split('T')[0],
      contactType: isPlanterContactType(contact.contactType) ? contact.contactType : 'other',
      notes: contact.notes,
      followUpDate: contact.followUpDate ? contact.followUpDate.split('T')[0] : '',
    });
    setShowContactForm(true);
  };

  const handlePhotoUpload = async (kind: 'headshot' | 'family', file: File) => {
    const errorKey = kind;
    setPhotoUploadError((prev) => ({ ...prev, [errorKey]: null }));
    setPhotoUploadLoading((prev) => ({ ...prev, [errorKey]: true }));

    try {
      const preparedFile = await prepareImageForUpload(file);
      if (kind === 'headshot') {
        await uploadPlanterHeadshot(planterId, preparedFile);
      } else {
        await uploadPlanterFamilyPhoto(planterId, preparedFile);
      }
      await queryClient.invalidateQueries({ queryKey: ['planter', planterId] });
    } catch (error) {
      const ax = error as {
        response?: { status?: number; data?: { message?: string } };
        code?: string;
        message?: string;
      };
      let message = ax.message || 'Failed to upload image.';
      if (ax.code === 'ECONNABORTED' || message.toLowerCase().includes('timeout')) {
        message = 'Upload timed out. Try a smaller image and upload again.';
      } else if (ax.response?.status === 413) {
        message = 'Image file is too large. Try a smaller image or compress it before uploading.';
      } else if (typeof ax.response?.data?.message === 'string' && ax.response.data.message.length > 0) {
        message = ax.response.data.message;
      }
      setPhotoUploadError((prev) => ({ ...prev, [errorKey]: message }));
    } finally {
      setPhotoUploadLoading((prev) => ({ ...prev, [errorKey]: false }));
    }
  };

  const getContactTypeIcon = (type: string) => {
    const icons: Record<string, React.ReactElement> = {
      phone: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      email: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      'in-person': (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      text: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
      other: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01" />
        </svg>
      ),
      video: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
    };
    return icons[type] || icons.phone;
  };

  if (!planterIdValid) {
    return (
      <Layout>
        <div className="text-center py-12 px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid planter</h2>
          <p className="text-gray-600 mb-4">This link does not include a valid planter id.</p>
          <button
            type="button"
            onClick={() => navigate('/planters')}
            className="text-primary-600 hover:text-primary-700"
          >
            Back to Planters
          </button>
        </div>
      </Layout>
    );
  }

  if (planterLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  if (planterQueryError) {
    return (
      <Layout>
        <div className="text-center py-12 px-4 max-w-lg mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Could not load planter</h2>
          <p className="text-gray-600 mb-4">{axiosErrorMessage(planterFetchError)}</p>
          <button
            type="button"
            onClick={() => navigate('/planters')}
            className="text-primary-600 hover:text-primary-700"
          >
            Back to Planters
          </button>
        </div>
      </Layout>
    );
  }

  if (!planter) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Planter Not Found</h2>
          <button
            type="button"
            onClick={() => navigate('/planters')}
            className="text-primary-600 hover:text-primary-700"
          >
            Back to Planters
          </button>
        </div>
      </Layout>
    );
  }

  const hasLinkedAccount =
    planter.hasPlanterAppUserLinked === true ||
    (planter.hasPlanterAppUserLinked === undefined &&
      Boolean(planter.user?.id || planter.userId));
  const canSendAppInvite =
    hasRole('admin') ||
    hasRole('staff') ||
    (hasRole('hub_leader') && planter.assignedHubLeader === user?.id);
  const canPipelineAct =
    hasRole('admin') ||
    hasRole('staff') ||
    (hasRole('hub_leader') && planter.assignedHubLeader === user?.id);
  const canManageReports = isStaffish || (hasRole('hub_leader') && planter.assignedHubLeader === user?.id);
  const canSendReportReminder =
    canManageReports &&
    hasLinkedAccount &&
    (planter.effectivePlantingStep ?? planter.plantingStep) === 'funding';
  const contactTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      phone: 'Phone Call',
      text: 'Text Message',
      email: 'Email',
      'in-person': 'In-Person',
      other: 'Other',
      video: 'Video',
      'hub-checkin': 'Hub Check-in',
    };
    return labels[type] || type;
  };

  const renderActionStack = () => (
    <>
          {pipelineMessage && (
            <p className="text-sm text-red-600 mt-2">{pipelineMessage}</p>
          )}

          {canEdit && canPipelineAct && !planter.pathKind && !planter.journeyEndedAt && (
            <div className="mt-4 rounded-lg border border-primary-200 bg-primary-50/90 p-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-2">Set pipeline</h2>
              <p className="text-xs text-gray-600 mb-3">
                This profile does not have a path yet. Choose growth or planting (and step) so they appear in the right pipeline column.
              </p>
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 items-stretch sm:items-end">
                <div className="min-w-[10rem]">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Path</label>
                  <select
                    value={pipelineBootstrapForm.pathKind}
                    onChange={(e) =>
                      setPipelineBootstrapForm((f) => ({
                        ...f,
                        pathKind: e.target.value as '' | 'growth' | 'planting',
                      }))
                    }
                    className="block w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="">Select…</option>
                    <option value="growth">Growth path</option>
                    <option value="planting">Planting path</option>
                  </select>
                </div>
                {pipelineBootstrapForm.pathKind === 'planting' && (
                  <>
                    <div className="min-w-[10rem]">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Step</label>
                      <select
                        value={pipelineBootstrapForm.plantingStep}
                        onChange={(e) =>
                          setPipelineBootstrapForm((f) => ({
                            ...f,
                            plantingStep: e.target.value as 'training' | 'funding' | 'post_funding',
                          }))
                        }
                        className="block w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-primary-500 focus:ring-primary-500"
                      >
                        <option value="training">Training</option>
                        <option value="funding">Funding</option>
                        <option value="post_funding">Post-funding</option>
                      </select>
                    </div>
                    {pipelineBootstrapForm.plantingStep !== 'training' && (
                      <div className="min-w-[10rem]">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Funding end (optional)
                        </label>
                        <input
                          type="date"
                          value={pipelineBootstrapForm.fundingEndDate}
                          onChange={(e) =>
                            setPipelineBootstrapForm((f) => ({ ...f, fundingEndDate: e.target.value }))
                          }
                          className="block w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-primary-500 focus:ring-primary-500"
                        />
                      </div>
                    )}
                  </>
                )}
                <button
                  type="button"
                  disabled={bootstrapPipelineMutation.isPending || !pipelineBootstrapForm.pathKind}
                  onClick={() => {
                    if (!pipelineBootstrapForm.pathKind) {
                      setPipelineMessage('Choose growth or planting path.');
                      return;
                    }
                    const payload: Partial<Planter> = { pathKind: pipelineBootstrapForm.pathKind };
                    if (pipelineBootstrapForm.pathKind === 'planting') {
                      payload.plantingStep = pipelineBootstrapForm.plantingStep;
                      if (
                        pipelineBootstrapForm.plantingStep !== 'training' &&
                        pipelineBootstrapForm.fundingEndDate
                      ) {
                        payload.fundingEndDate = pipelineBootstrapForm.fundingEndDate;
                      }
                    }
                    bootstrapPipelineMutation.mutate(payload);
                  }}
                  className="inline-flex justify-center items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 sm:self-end"
                >
                  {bootstrapPipelineMutation.isPending ? 'Saving…' : 'Save pipeline'}
                </button>
              </div>
            </div>
          )}

          {planter.journeyEndedAt && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Journey ended ({formatJourneyEndReasonLabel(planter.journeyEndReason)}) on{' '}
              {new Date(planter.journeyEndedAt).toLocaleString()}
            </div>
          )}

          {canEdit && canPipelineAct && !planter.journeyEndedAt && (
            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Pipeline actions</h2>
              <div className={`flex flex-wrap gap-2 ${isLg ? 'lg:flex-col lg:items-stretch' : ''}`}>
                {planter.pathKind === 'growth' && (
                  <button
                    type="button"
                    disabled={promoteFromGrowthMutation.isPending}
                    onClick={() => promoteFromGrowthMutation.mutate()}
                    className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                  >
                    Promote to planter
                  </button>
                )}
                {planter.pathKind === 'planting' &&
                  (planter.effectivePlantingStep ?? planter.plantingStep) === 'training' && (
                    <button
                      type="button"
                      disabled={completeTrainingMutation.isPending}
                      onClick={() => completeTrainingMutation.mutate()}
                      className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                    >
                      Mark training complete
                    </button>
                  )}
                {planter.pathKind === 'planting' &&
                  (planter.effectivePlantingStep ?? planter.plantingStep) === 'post_funding' && (
                    <button
                      type="button"
                      disabled={revertToFundingMutation.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            'Move this planter back to funding? Funding end date will be cleared.',
                          )
                        ) {
                          revertToFundingMutation.mutate();
                        }
                      }}
                      className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-800 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Revert to funding
                    </button>
                  )}
                {planter.pathKind === 'planting' && planter.plantingStep === 'funding' && (
                    <>
                      <input
                        type="date"
                        value={fundingEndInput}
                        onChange={(e) => setFundingEndInput(e.target.value)}
                        className="rounded-md border border-gray-300 px-2 py-2 text-sm"
                      />
                      <button
                        type="button"
                        disabled={!fundingEndInput || setFundingEndMutation.isPending}
                        onClick={() => setFundingEndMutation.mutate()}
                        className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                      >
                        Save funding end date
                      </button>
                      <button
                        type="button"
                        disabled={advancePostFundingMutation.isPending}
                        onClick={() => advancePostFundingMutation.mutate()}
                        className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-slate-700 hover:bg-slate-800 disabled:opacity-50"
                      >
                        Advance to post-funding
                      </button>
                      <button
                        type="button"
                        disabled={revertToTrainingMutation.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              'Move this planter back to training? Funding end date and training completion date will be cleared.',
                            )
                          ) {
                            revertToTrainingMutation.mutate();
                          }
                        }}
                        className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-800 bg-white hover:bg-gray-50 disabled:opacity-50"
                      >
                        Revert to training
                      </button>
                    </>
                  )}
                <div className="relative inline-block" ref={endJourneyMenuRef}>
                  <button
                    type="button"
                    disabled={endJourneyMutation.isPending}
                    onClick={() => setEndJourneyMenuOpen((o) => !o)}
                    className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border border-red-400 text-red-900 bg-white hover:bg-red-50 disabled:opacity-50"
                  >
                    End journey
                    <span className="ml-1" aria-hidden>
                      ▾
                    </span>
                  </button>
                  {endJourneyMenuOpen && (
                    <ul
                      className="absolute left-0 z-20 mt-1 min-w-[11rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg"
                      role="menu"
                    >
                      {END_JOURNEY_OPTIONS.map(({ reason, label }) => (
                        <li key={reason}>
                          <button
                            type="button"
                            role="menuitem"
                            className="block w-full px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-100"
                            onClick={() => {
                              setEndJourneyMenuOpen(false);
                              if (reason === 'other') {
                                setEndJourneyOtherNotes('');
                                setShowEndJourneyOtherModal(true);
                                return;
                              }
                              const msg = `End this planter journey as ${label.toLowerCase()}?`;
                              if (window.confirm(msg)) {
                                endJourneyMutation.mutate({ reason });
                              }
                            }}
                          >
                            {label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Action Buttons */}
          {(canEdit || canDelete) && (
            <div className={`mt-4 pt-4 border-t border-gray-200 flex gap-3 ${isLg ? 'flex-col' : 'items-center justify-end'}`}>
              {canEdit && (
                <button
                  onClick={() => {
                    setEditForm({
                      name: planter.name || '',
                      spouseName: planter.spouseName || '',
                      email: planter.email || '',
                      phone: planter.phone || '',
                      secondaryPhone: planter.secondaryPhone || '',
                      city: planter.city || '',
                      state: planter.state || '',
                      churchName: planter.churchName || '',
                      mailingAddressStreet: planter.mailingAddressStreet || '',
                      mailingAddressStreet2: planter.mailingAddressStreet2 || '',
                      mailingAddressCity: planter.mailingAddressCity || '',
                      mailingAddressState: planter.mailingAddressState || '',
                      mailingAddressZip: planter.mailingAddressZip || '',
                      mailingAddressCountry: planter.mailingAddressCountry || '',
                      assignedCoachId: planter.coach?.id ? String(planter.coach.id) : '',
                    });
                    setShowEditModal(true);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Planter
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this planter? This action cannot be undone.')) {
                      deletePlanterMutation.mutate();
                    }
                  }}
                  className="inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Planter
                </button>
              )}
            </div>
          )}

            {canSendAppInvite && !hasLinkedAccount && (
              <div className="bg-white shadow rounded-lg p-6 border border-primary-100">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Plant app access</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Send an email with a secure link so this planter can sign in to the plant app (monthly reports and profile).
                  No portal candidate onboarding is required.
                </p>
                {!planter.email?.trim() && (
                  <p className="text-sm text-amber-700 mb-3">
                    Add an email address on this profile before sending an invite.
                  </p>
                )}
                {inviteAppMutation.isError && (
                  <p className="text-sm text-red-600 mb-3">
                    {(inviteAppMutation.error as Error)?.message || 'Could not send invite.'}
                  </p>
                )}
                <button
                  type="button"
                  disabled={!planter.email?.trim() || inviteAppMutation.isPending}
                  onClick={() => inviteAppMutation.mutate()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {inviteAppMutation.isPending ? 'Sending…' : 'Send invitation email'}
                </button>
                {planter.appInvite && (
                  <p className="text-sm text-gray-600 mt-3">
                    {planter.appInvite.usedAt
                      ? `Previous invite was used on ${new Date(planter.appInvite.usedAt).toLocaleString()}.`
                      : `Latest invite pending; expires ${new Date(planter.appInvite.expiresAt).toLocaleString()}.`}
                  </p>
                )}
              </div>
            )}
    </>
  );

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/planters')}
            className="text-sm text-gray-600 hover:text-gray-900 mb-4 flex items-center"
          >
            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Planters
          </button>

          <div className="mb-2 pb-10">
            <div className="relative">
              <div className="relative h-[180px] rounded-2xl overflow-hidden shadow-md">
                {planter.familyPhotoImagePath ? (
                  <img
                    src={getImageUrl(planter.familyPhotoImagePath, { width: 1600, quality: 70, format: 'webp' })}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-purple-600" aria-hidden />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/25 to-transparent pointer-events-none" />
              </div>

              <div className="pointer-events-none absolute top-3 right-3 z-20 flex max-w-[min(100%,14rem)] flex-col items-end gap-2 sm:max-w-[55%]">
                <span
                  className={`pointer-events-auto rounded-full px-3 py-1 text-xs font-semibold shadow ${
                    hasLinkedAccount
                      ? 'bg-emerald-500/95 text-white'
                      : 'bg-amber-500/95 text-white'
                  }`}
                  title={
                    hasLinkedAccount && planter.user?.email
                      ? `Linked: ${planter.user.email}`
                      : undefined
                  }
                >
                  {hasLinkedAccount ? 'App account linked' : 'No app account'}
                </span>
                {canSendAppInvite && !hasLinkedAccount && (
                  <button
                    type="button"
                    disabled={!planter.email?.trim() || inviteAppMutation.isPending}
                    onClick={() => inviteAppMutation.mutate()}
                    title={!planter.email?.trim() ? 'Add an email on this profile to send an invite' : undefined}
                    className="pointer-events-auto inline-flex items-center rounded-md border border-white/40 bg-white/95 px-3 py-1.5 text-sm font-medium text-primary-700 shadow hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 lg:hidden"
                  >
                    {inviteAppMutation.isPending ? 'Sending…' : 'Invite to app'}
                  </button>
                )}
              </div>

              <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 flex items-end gap-3 p-4 sm:gap-4">
                <Avatar
                  name={planter.name}
                  size="2xl"
                  imageUrl={getImageUrl(planter.headshotImagePath, { width: 256, quality: 70, format: 'webp' })}
                  className="pointer-events-auto shrink-0 translate-y-7 shadow-lg ring-4 ring-white"
                />
                <div className="pointer-events-auto mb-0.5 min-w-0 flex-1 rounded-2xl border border-white/25 bg-black/25 px-3 py-2 text-white shadow-lg backdrop-blur-md">
                  <h1 className="text-2xl font-bold leading-tight">{planter.name}</h1>
                  {planter.spouseName && <p className="mt-0.5 text-sm text-white/90">& {planter.spouseName}</p>}
                  {planter.churchName && <p className="mt-1 text-sm text-white/80">{planter.churchName}</p>}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-2 flex flex-wrap gap-2">
            {planter.pathKind && (
              <span
                className={`px-3 py-1 text-sm font-semibold rounded-full ${
                  planter.pathKind === 'growth'
                    ? 'bg-amber-100 text-amber-900'
                    : 'bg-emerald-100 text-emerald-900'
                }`}
              >
                {planter.pathKind === 'growth' ? 'Growth path' : 'Planting path'}
              </span>
            )}
            {planter.pathKind === 'planting' && (
              <span className="px-3 py-1 text-sm font-semibold rounded-full bg-slate-100 text-slate-800">
                Step: {plantingStepLabel(planter.effectivePlantingStep ?? planter.plantingStep)}
              </span>
            )}
          </div>


        </div>

        {!isLg && (
          <div className="space-y-4 mb-6">
            {renderActionStack()}
          </div>
        )}

        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 lg:items-start">
          {/* Main Info Column */}
          <div className="lg:col-span-2 space-y-6">

            {/* Basic Information */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">City / State</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatPlanterLocation(planter) || 'Not specified'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Church Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{planter.churchName || 'Not specified'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {planter.email?.trim() || planter.user?.email || 'Not available'}
                  </dd>
                  {planter.user?.email &&
                    planter.email?.trim() &&
                    planter.email.trim().toLowerCase() !== planter.user.email.trim().toLowerCase() && (
                      <p className="mt-1 text-xs text-gray-500">Linked account: {planter.user.email}</p>
                    )}
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Phone</dt>
                  <dd className="mt-1 text-sm text-gray-900">{planter.phone?.trim() || planter.user?.phone || 'Not available'}</dd>
                </div>
                {planter.secondaryPhone?.trim() && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Secondary phone</dt>
                    <dd className="mt-1 text-sm text-gray-900">{planter.secondaryPhone}</dd>
                  </div>
                )}
                {[
                  planter.mailingAddressStreet,
                  planter.mailingAddressStreet2,
                  [
                    planter.mailingAddressCity,
                    planter.mailingAddressState,
                    planter.mailingAddressZip,
                  ]
                    .filter(Boolean)
                    .join(' '),
                  planter.mailingAddressCountry,
                ]
                  .map((line) => (typeof line === 'string' ? line.trim() : ''))
                  .filter(Boolean).length > 0 && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Mailing address</dt>
                    <dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">
                      {[
                        planter.mailingAddressStreet,
                        planter.mailingAddressStreet2,
                        [
                          planter.mailingAddressCity,
                          planter.mailingAddressState,
                          planter.mailingAddressZip,
                        ]
                          .filter(Boolean)
                          .join(' '),
                        planter.mailingAddressCountry,
                      ]
                        .map((line) => (typeof line === 'string' ? line.trim() : ''))
                        .filter(Boolean)
                        .join('\n')}
                    </dd>
                  </div>
                )}
                {planter.coach && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Coach</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {planter.coach.firstName} {planter.coach.lastName}
                    </dd>
                  </div>
                )}
                {planter.assignedHub && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Hub</dt>
                    <dd className="mt-1 text-sm text-gray-900">{planter.assignedHub}</dd>
                  </div>
                )}
                {(planter.latitude != null && planter.longitude != null) && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Coordinates</dt>
                    <dd className="mt-1 text-sm text-gray-900">{Number(planter.latitude).toFixed(5)}, {Number(planter.longitude).toFixed(5)}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Ministry reports (MBCB) */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between gap-3 mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Ministry reports</h2>
                {canManageReports && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddReportForm((open) => !open);
                      setAddReportMessage(null);
                    }}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Add report
                  </button>
                )}
              </div>
              {addReportMessage && (
                <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {addReportMessage}
                </div>
              )}
              {reportReminderMessage && (
                <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                  {reportReminderMessage}
                </div>
              )}
              {showAddReportForm && canManageReports && (
                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                    <label className="block">
                      <span className="block text-sm font-medium text-gray-700 mb-1">Month</span>
                      <input
                        type="number"
                        min={1}
                        max={12}
                        value={newReportForm.reportMonth}
                        onChange={(e) =>
                          setNewReportForm((prev) => ({
                            ...prev,
                            reportMonth: Math.max(1, Math.min(12, Number.parseInt(e.target.value, 10) || 1)),
                          }))
                        }
                        className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="block text-sm font-medium text-gray-700 mb-1">Year</span>
                      <input
                        type="number"
                        min={2000}
                        max={2100}
                        value={newReportForm.reportYear}
                        onChange={(e) =>
                          setNewReportForm((prev) => ({
                            ...prev,
                            reportYear: Number.parseInt(e.target.value, 10) || new Date().getFullYear(),
                          }))
                        }
                        className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => createReportMutation.mutate()}
                      disabled={createReportMutation.isPending}
                      className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                    >
                      {createReportMutation.isPending ? 'Creating…' : 'Create & open'}
                    </button>
                  </div>
                </div>
              )}
              {mbcbLoading ? (
                <p className="text-sm text-gray-500">Loading reports…</p>
              ) : sortedMbcbReports.length > 0 ? (
                <div className="space-y-6">
                  {mbcbReportDetailsLoading && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                      Loading report insights…
                    </div>
                  )}

                  {reportInsights && (
                    <>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Reports on file</div>
                          <div className="mt-2 text-2xl font-bold text-gray-900">{reportInsights.reportCount}</div>
                          <div className="mt-1 text-sm text-gray-600">
                            Latest: {formatMonthYear(reportInsights.latestReport.reportMonth, reportInsights.latestReport.reportYear)}
                          </div>
                        </div>
                        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                          <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">Attendance average</div>
                          <div className="mt-2 text-2xl font-bold text-blue-900">
                            {Math.round(reportInsights.attendanceAverage)}
                          </div>
                          <div className="mt-1 text-sm text-blue-800">Average worship attendance per Sunday</div>
                        </div>
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Total received</div>
                          <div className="mt-2 text-2xl font-bold text-emerald-900">{formatMoney(reportInsights.totalReceived)}</div>
                          <div className="mt-1 text-sm text-emerald-800">Across all loaded monthly reports</div>
                        </div>
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                          <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">Cooperative Program</div>
                          <div className="mt-2 text-2xl font-bold text-amber-900">{formatMoney(reportInsights.cpGiving)}</div>
                          <div className="mt-1 text-sm text-amber-800">
                            {formatPercent(reportInsights.cpPercentageOfReceived)} of total received
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.35fr_1fr]">
                        <div className="space-y-3">
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900">Attendance trend</h3>
                            <p className="text-sm text-gray-600">Average worship attendance by reporting period.</p>
                          </div>
                          <AttendanceTrendChart points={reportInsights.attendanceSeries} />
                        </div>

                        <div className="space-y-3">
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900">Giving breakdown by source</h3>
                            <p className="text-sm text-gray-600">Combined budget receipts across submitted report periods.</p>
                          </div>
                          <GivingBreakdownPieChart
                            slices={reportInsights.sourceBreakdown.map((source) => ({
                              label: source.label,
                              value: source.value,
                            }))}
                            total={reportInsights.totalReceived}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {detailedReports.length > 0 ? (
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Monthly report snapshots</h3>
                        <p className="text-sm text-gray-600">Recent ministry reporting is visible directly on the planter profile.</p>
                      </div>
                      {detailedReports.map((report) => {
                        const canEditReport = canEditMbcbReport(report.status, canManageReports);
                        const reportHref = canEditReport
                          ? `/planters/${planterId}/reports/${report.id}/edit`
                          : `/planters/${planterId}/reports/${report.id}`;
                        const totalReceived = getIncomeTotal(report);
                        const cpGiving = getCpGiving(report);
                        const cpShare = totalReceived > 0 ? (cpGiving / totalReceived) * 100 : null;

                        return (
                          <div key={report.id} className="rounded-xl border border-gray-200 p-4 shadow-sm">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <h4 className="text-lg font-semibold text-gray-900">
                                {formatMonthYear(report.reportMonth, report.reportYear)}
                              </h4>
                              <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusClassName(report.status)}`}>
                                {report.status}
                              </span>
                              {canSendReportReminder && (report.status === 'draft' || report.status === 'needs_revision') && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setReportReminderMessage(null);
                                    sendReminderMutation.mutate(report.id);
                                  }}
                                  disabled={sendReminderMutation.isPending}
                                  className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                                >
                                  {sendReminderMutation.isPending ? 'Sending reminder…' : 'Send reminder'}
                                </button>
                              )}
                            </div>
                                <p className="mt-1 text-sm text-gray-500">
                                  Avg attendance {Math.round(getAttendanceAverage(report))} • Total received {formatMoney(totalReceived)}
                                </p>
                              </div>
                            <Link
                                to={reportHref}
                                className="text-sm font-medium text-primary-700 hover:text-primary-800"
                              >
                                {mbcbReportActionLabel(report.status)}
                              </Link>
                            </div>

                            <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                              <div className="rounded-lg bg-gray-50 p-3">
                                <div className="text-xs text-gray-500">Worship avg</div>
                                <div className="mt-1 text-lg font-semibold text-gray-900">{Math.round(getAttendanceAverage(report))}</div>
                              </div>
                              <div className="rounded-lg bg-gray-50 p-3">
                                <div className="text-xs text-gray-500">Total received</div>
                                <div className="mt-1 text-lg font-semibold text-gray-900">{formatMoney(totalReceived)}</div>
                              </div>
                              <div className="rounded-lg bg-gray-50 p-3">
                                <div className="text-xs text-gray-500">CP giving</div>
                                <div className="mt-1 text-lg font-semibold text-gray-900">{formatMoney(cpGiving)}</div>
                              </div>
                              <div className="rounded-lg bg-gray-50 p-3">
                                <div className="text-xs text-gray-500">CP share of receipts</div>
                                <div className="mt-1 text-lg font-semibold text-gray-900">{formatPercent(cpShare)}</div>
                              </div>
                            </div>

                            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                              <div className="rounded-lg border border-amber-100 bg-amber-50/70 p-4">
                                <div className="text-xs font-semibold uppercase tracking-wide text-amber-800">Celebrate</div>
                                <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800">{report.ministryHighlights || '—'}</p>
                              </div>
                              <div className="rounded-lg border border-gray-200 p-4">
                                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Pray with them about</div>
                                <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800">{report.prayerRequests || '—'}</p>
                              </div>
                              <div className="rounded-lg border border-gray-200 p-4">
                                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">How the network can help</div>
                                <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800">{report.supportNeeded || '—'}</p>
                              </div>
                              <div className="rounded-lg border border-gray-200 p-4">
                                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Anything else</div>
                                <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800">{report.additionalNotes || '—'}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {sortedMbcbReports.map((report) => {
                        const canEditReport = canEditMbcbReport(report.status, canManageReports);
                        const reportHref = canEditReport
                          ? `/planters/${planterId}/reports/${report.id}/edit`
                          : `/planters/${planterId}/reports/${report.id}`;

                        return (
                          <li key={report.id} className="py-2 flex items-center justify-between gap-2">
                            <span className="text-sm text-gray-800">
                              {formatMonthYear(report.reportMonth, report.reportYear)}
                              <span className="text-gray-500 ml-2">({mbcbReportStatusLabel(report.status)})</span>
                            </span>
                            <div className="flex items-center gap-3">
                              {canSendReportReminder && (report.status === 'draft' || report.status === 'needs_revision') && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setReportReminderMessage(null);
                                    sendReminderMutation.mutate(report.id);
                                  }}
                                  disabled={sendReminderMutation.isPending}
                                  className="text-sm font-medium text-blue-700 hover:text-blue-800 disabled:opacity-50"
                                >
                                  {sendReminderMutation.isPending ? 'Sending…' : 'Remind'}
                                </button>
                              )}
                              <Link
                                to={reportHref}
                                className="text-sm font-medium text-primary-600 hover:text-primary-800"
                              >
                                {mbcbReportActionLabel(report.status)}
                              </Link>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No monthly reports yet.</p>
              )}
            </div>

            {/* Contact History */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Contact History</h2>
                <button
                  onClick={() => {
                    setShowContactForm(!showContactForm);
                    setEditingContact(null);
                    resetContactForm();
                  }}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Contact
                </button>
              </div>

              {/* Contact Form */}
              {showContactForm && (
                <form onSubmit={handleSubmitContact} className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">
                    {editingContact ? 'Edit Contact' : 'New Contact'}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Date
                      </label>
                      <input
                        type="date"
                        required
                        value={contactForm.contactDate}
                        onChange={(e) => setContactForm({ ...contactForm, contactDate: e.target.value })}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Type
                      </label>
                      <select
                        required
                        value={contactForm.contactType}
                        onChange={(e) => setContactForm({ ...contactForm, contactType: e.target.value as PlanterContactType })}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      >
                        {contactTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      required
                      rows={3}
                      value={contactForm.notes}
                      onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      placeholder="What did you discuss?"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Follow-up Date (Optional)
                    </label>
                    <input
                      type="date"
                      value={contactForm.followUpDate}
                      onChange={(e) => setContactForm({ ...contactForm, followUpDate: e.target.value })}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowContactForm(false);
                        setEditingContact(null);
                        resetContactForm();
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createContactMutation.isPending || updateContactMutation.isPending}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
                    >
                      {editingContact ? 'Update' : 'Save'} Contact
                    </button>
                  </div>
                </form>
              )}

              {/* Contact List */}
              {contactsLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                </div>
              ) : contacts && contacts.length > 0 ? (
                <div className="space-y-3">
                  {contacts.map((contact) => (
                    <div key={contact.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          <div className="flex-shrink-0 text-gray-400">
                            {getContactTypeIcon(contact.contactType)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-sm font-medium text-gray-900">
                                {contactTypeLabel(contact.contactType)}
                              </p>
                              <p className="text-sm text-gray-500">
                                {new Date(contact.contactDate).toLocaleDateString()}
                              </p>
                            </div>
                            <p className="text-sm text-gray-600">{contact.notes}</p>
                            {contact.followUpDate && (
                              <p className="text-xs text-gray-500 mt-2">
                                Follow-up: {new Date(contact.followUpDate).toLocaleDateString()}
                                {contact.followUpCompleted && (
                                  <span className="ml-2 text-green-600">✓ Completed</span>
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="ml-4 flex-shrink-0 flex space-x-2">
                          <button
                            onClick={() => handleEditContact(contact)}
                            className="text-sm text-primary-600 hover:text-primary-700"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this contact?')) {
                                deleteContactMutation.mutate(contact.id);
                              }
                            }}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No contact history yet.</p>
                  <p className="text-sm mt-2">Add a contact to start tracking interactions.</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar Column */}
          <aside className="lg:col-span-1 space-y-6 lg:sticky lg:top-24 lg:self-start">
            {isLg && (
              <div className="space-y-4">{renderActionStack()}</div>
            )}
            {/* Notes */}
            {planter.notes && (
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Notes</h2>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{planter.notes}</p>
              </div>
            )}

            {/* Important Dates */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Important Dates</h2>
              <dl className="space-y-3">
                {planter.startDate && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Start Date</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {new Date(planter.startDate).toLocaleDateString()}
                    </dd>
                  </div>
                )}
                {planter.graduationDate && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Graduation Date</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {new Date(planter.graduationDate).toLocaleDateString()}
                    </dd>
                  </div>
                )}
                {planter.lastContactedAt && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Last Contacted</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {new Date(planter.lastContactedAt).toLocaleDateString()}
                    </dd>
                  </div>
                )}
                {planter.nextFollowUpDate && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Next Follow-up</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {new Date(planter.nextFollowUpDate).toLocaleDateString()}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </aside>
        </div>

        {/* End journey (other reason) — optional notes */}
        {showEndJourneyOtherModal && planter && canEdit && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => !endJourneyMutation.isPending && setShowEndJourneyOtherModal(false)}
              />
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">End journey (other)</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Optionally add a short note for why this journey ended as &quot;other&quot;.
                  </p>
                  <textarea
                    value={endJourneyOtherNotes}
                    onChange={(e) => setEndJourneyOtherNotes(e.target.value)}
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    placeholder="Optional notes"
                  />
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                  <button
                    type="button"
                    disabled={endJourneyMutation.isPending}
                    onClick={() => {
                      endJourneyMutation.mutate({
                        reason: 'other',
                        notes: endJourneyOtherNotes.trim() || undefined,
                      });
                    }}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    End journey
                  </button>
                  <button
                    type="button"
                    disabled={endJourneyMutation.isPending}
                    onClick={() => setShowEndJourneyOtherModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Planter Modal */}
        {showEditModal && planter && canEdit && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowEditModal(false)}></div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full max-h-[90vh] overflow-y-auto">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const payload: Partial<Planter> = {
                      name: editForm.name.trim(),
                      spouseName: editForm.spouseName.trim() || null,
                      email: editForm.email.trim() || null,
                      phone: editForm.phone.trim() || null,
                      secondaryPhone: editForm.secondaryPhone.trim() || null,
                      city: editForm.city.trim() || null,
                      state: editForm.state.trim() || null,
                      churchName: editForm.churchName.trim() || null,
                      mailingAddressStreet: editForm.mailingAddressStreet.trim() || null,
                      mailingAddressStreet2: editForm.mailingAddressStreet2.trim() || null,
                      mailingAddressCity: editForm.mailingAddressCity.trim() || null,
                      mailingAddressState: editForm.mailingAddressState.trim() || null,
                      mailingAddressZip: editForm.mailingAddressZip.trim() || null,
                      mailingAddressCountry: editForm.mailingAddressCountry.trim() || null,
                      assignedCoach: editForm.assignedCoachId
                        ? parseInt(editForm.assignedCoachId, 10)
                        : null,
                    };
                    updatePlanterMutation.mutate(payload);
                  }}
                >
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Edit Church Planter</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Full Name</label>
                        <input
                          type="text"
                          required
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Spouse Name</label>
                        <input
                          type="text"
                          value={editForm.spouseName}
                          onChange={(e) => setEditForm({ ...editForm, spouseName: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Email</label>
                          <input
                            type="email"
                            value={editForm.email}
                            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Phone</label>
                          <input
                            type="text"
                            value={editForm.phone}
                            onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Secondary phone</label>
                        <input
                          type="text"
                          value={editForm.secondaryPhone}
                          onChange={(e) => setEditForm({ ...editForm, secondaryPhone: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">City</label>
                          <input
                            type="text"
                            value={editForm.city}
                            onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">State</label>
                          <input
                            type="text"
                            value={editForm.state}
                            onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Church Name</label>
                        <input
                          type="text"
                          value={editForm.churchName}
                          onChange={(e) => setEditForm({ ...editForm, churchName: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div className="border-t border-gray-200 pt-4">
                        <p className="text-sm font-medium text-gray-900 mb-3">Profile photos</p>
                        <p className="text-xs text-gray-500 mb-4">
                          Headshot and family photo also appear at the top of this profile.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700">Headshot</span>
                              {planter.headshotImagePath && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (window.confirm('Remove headshot?')) {
                                      await deletePlanterHeadshot(planter.id);
                                      queryClient.invalidateQueries({ queryKey: ['planter', planterId] });
                                    }
                                  }}
                                  className="text-sm text-red-600 hover:text-red-700"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                            {planter.headshotImagePath ? (
                              <img
                                src={getImageUrl(planter.headshotImagePath, { width: 800, quality: 70, format: 'webp' })}
                                alt="Headshot"
                                className="w-full h-48 object-cover rounded-lg border"
                              />
                            ) : (
                              <p className="text-sm text-gray-500">No headshot uploaded.</p>
                            )}
                            <div className="mt-3">
                              <input
                                id="edit-headshot-upload"
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  await handlePhotoUpload('headshot', file);
                                  (e.target as HTMLInputElement).value = '';
                                }}
                              />
                              <label
                                htmlFor="edit-headshot-upload"
                                className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white cursor-pointer ${
                                  photoUploadLoading.headshot ? 'bg-primary-400 cursor-wait' : 'bg-primary-600 hover:bg-primary-700'
                                }`}
                              >
                                {photoUploadLoading.headshot
                                  ? 'Uploading...'
                                  : planter.headshotImagePath ? 'Change Headshot' : 'Upload Headshot'}
                              </label>
                              {photoUploadError.headshot && (
                                <p className="mt-2 text-sm text-red-600">{photoUploadError.headshot}</p>
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700">Family Photo</span>
                              {planter.familyPhotoImagePath && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (window.confirm('Remove family photo?')) {
                                      await deletePlanterFamilyPhoto(planter.id);
                                      queryClient.invalidateQueries({ queryKey: ['planter', planterId] });
                                    }
                                  }}
                                  className="text-sm text-red-600 hover:text-red-700"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                            {planter.familyPhotoImagePath ? (
                              <>
                                <img
                                  src={getImageUrl(planter.familyPhotoImagePath, { width: 1200, quality: 70, format: 'webp' })}
                                  alt="Family"
                                  className="w-full h-48 object-cover rounded-lg border"
                                />
                                <p className="mt-2 text-xs text-gray-500">Shown as cover on this profile.</p>
                              </>
                            ) : (
                              <p className="text-sm text-gray-500">No family photo uploaded.</p>
                            )}
                            <div className="mt-3">
                              <input
                                id="edit-family-upload"
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  await handlePhotoUpload('family', file);
                                  (e.target as HTMLInputElement).value = '';
                                }}
                              />
                              <label
                                htmlFor="edit-family-upload"
                                className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white cursor-pointer ${
                                  photoUploadLoading.family ? 'bg-primary-400 cursor-wait' : 'bg-primary-600 hover:bg-primary-700'
                                }`}
                              >
                                {photoUploadLoading.family
                                  ? 'Uploading...'
                                  : planter.familyPhotoImagePath ? 'Change Family Photo' : 'Upload Family Photo'}
                              </label>
                              {photoUploadError.family && (
                                <p className="mt-2 text-sm text-red-600">{photoUploadError.family}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="border-t border-gray-200 pt-4">
                        <p className="text-sm font-medium text-gray-900 mb-3">Mailing address</p>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Street</label>
                            <input
                              type="text"
                              value={editForm.mailingAddressStreet}
                              onChange={(e) => setEditForm({ ...editForm, mailingAddressStreet: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Street line 2</label>
                            <input
                              type="text"
                              value={editForm.mailingAddressStreet2}
                              onChange={(e) => setEditForm({ ...editForm, mailingAddressStreet2: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">City</label>
                              <input
                                type="text"
                                value={editForm.mailingAddressCity}
                                onChange={(e) => setEditForm({ ...editForm, mailingAddressCity: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">State</label>
                              <input
                                type="text"
                                value={editForm.mailingAddressState}
                                onChange={(e) => setEditForm({ ...editForm, mailingAddressState: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">ZIP</label>
                              <input
                                type="text"
                                value={editForm.mailingAddressZip}
                                onChange={(e) => setEditForm({ ...editForm, mailingAddressZip: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Country</label>
                              <input
                                type="text"
                                value={editForm.mailingAddressCountry}
                                onChange={(e) => setEditForm({ ...editForm, mailingAddressCountry: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Assign Coach</label>
                        <select
                          value={editForm.assignedCoachId}
                          onChange={(e) => setEditForm({ ...editForm, assignedCoachId: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        >
                          <option value="">Unassigned</option>
                          {coaches.map(c => (
                            <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      disabled={updatePlanterMutation.isPending}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      {updatePlanterMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PlanterDetail;
